import { ChangeDetectorRef, Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { SubscriptionService } from '@app/+authenticated/+reports/shared/subscriptions/subscription.service';
import { getAccountSubscription } from '@app/reducers/account/account-service.selectors';
import { DataStartup } from '@app/startup/data-startup.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { LogoComponent } from '@sb/ui/logo';
import { ProgressBarComponent } from '@sb/ui/progress-bar/progress-bar.component';
import { SpinnerComponent } from '@sb/ui/spinner';
import {
  InvoicePeriodType,
  PlanQuotaModel,
  PlanType,
  QuotaType,
  SubscriptionModel,
} from '@shiftbase-com/ts-shared/models';
import { Subscription } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

import { DowngradeEmployeesComponent } from './components/steps/downgrade-employees.component';
import { DowngradeIntroComponent } from './components/steps/downgrade-intro.component';
import { DowngradeLocationsComponent } from './components/steps/downgrade-locations.component';

enum DowngradeSteps {
  INTRO,
  LOCATIONS,
  EMPLOYEES,
}

@Component({
  selector: 'app-downgrade',
  imports: [
    ProgressBarComponent,
    SpinnerComponent,
    TranslatePipe,
    DowngradeIntroComponent,
    DowngradeLocationsComponent,
    DowngradeEmployeesComponent,
    LogoComponent,
  ],
  templateUrl: './downgrade-steps.component.html',
})
export class DowngradeComponent implements OnInit, OnDestroy {
  #dataStartup = inject(DataStartup);
  #store = inject<Store<AppState>>(Store);
  #cd = inject(ChangeDetectorRef);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #subscriptionService = inject(SubscriptionService);
  #destroyRef = inject(DestroyRef);
  #freeSubscription?: SubscriptionModel;

  steps: DowngradeSteps[] = [];

  downgradeSteps = DowngradeSteps;

  // Targeting Free (from trial-expired): the account isn't downgraded yet, so the intro copy differs
  // from the regular quota-action flow (where the downgrade has already happened).
  isFreeDowngrade = false;

  subscription?: SubscriptionModel;

  // Set when the flow targets Free (arriving from the trial-expired page): completing the last step
  // switches the account to this subscription. Left undefined for the regular quota-action downgrade.
  freePlanSubscriptionId?: string;

  currentStepIndex = 0;
  progress: number;

  dataSubs = new Subscription();

  ngOnInit() {
    this.dataSubs.add(this.#dataStartup.requiredLoader().subscribe());

    this.isFreeDowngrade = this.#route.snapshot.queryParamMap.get('plan') === PlanType.FREE;

    // The employees step only applies to Free (which caps seats); the regular quota-action flow
    // downgrades to a plan with unlimited seats, so it stops after locations/departments/teams.
    this.steps = this.isFreeDowngrade
      ? [DowngradeSteps.INTRO, DowngradeSteps.LOCATIONS, DowngradeSteps.EMPLOYEES]
      : [DowngradeSteps.INTRO, DowngradeSteps.LOCATIONS];

    if (this.isFreeDowngrade) {
      this.#loadFreePlanTarget();
    } else {
      this.dataSubs.add(
        this.#store.select(getAccountSubscription).subscribe((subscription) => {
          this.subscription = subscription;
          this.#cd.detectChanges();
        }),
      );
    }

    this.calculateProgress();
  }

  // Show the account's live usage against the Free plan's limits, so the steps guide the user to
  // remove the teams/departments that exceed Free. Reuses the same live subscription source as the
  // regular flow, so quota usage stays reactive as items are removed.
  #loadFreePlanTarget() {
    this.dataSubs.add(
      this.#subscriptionService
        .getSubscriptions()
        .pipe(
          take(1),
          tap((subscriptions) => {
            this.#freeSubscription = subscriptions.find((subscription) => subscription.plan.type === PlanType.FREE);
            this.freePlanSubscriptionId = this.#freeSubscription?.id;
          }),
          switchMap(() => this.#store.select(getAccountSubscription)),
        )
        .subscribe((subscription) => {
          if (subscription) {
            this.subscription = this.#withFreePlanLimits(subscription);
            this.#cd.detectChanges();
          }
        }),
    );
  }

  #withFreePlanLimits(subscription: SubscriptionModel): SubscriptionModel {
    const freeSubscription = this.#freeSubscription;

    if (!freeSubscription) {
      return subscription;
    }

    // Start from the Free plan's quotas (the account's usage measured against Free's limits, for every
    // quota Free caps — including ones the current plan doesn't expose, e.g. departments/teams on an
    // unlimited trial). Then let the account subscription's own entries take over where present, keeping
    // Free's limit, so reactive refetches (e.g. seats after deactivating employees) still flow through.
    const quotas: Partial<Record<QuotaType, PlanQuotaModel>> = { ...freeSubscription.plan.quotas };

    for (const quotaType of Object.keys(subscription.plan.quotas) as QuotaType[]) {
      const quota = subscription.plan.quotas[quotaType];
      const freeLimit = freeSubscription.plan.quotas[quotaType]?.limit;

      if (quota) {
        quotas[quotaType] = freeLimit !== undefined ? { ...quota, limit: freeLimit } : quota;
      }
    }

    return { ...subscription, plan: { ...subscription.plan, name: freeSubscription.plan.name, quotas } };
  }

  // Each step reports completion here; advance to the next step, or finalize once the last one is done.
  advance() {
    if (this.currentStepIndex < this.steps.length - 1) {
      this.incrementStep();
    } else {
      this.#finalize();
    }
  }

  incrementStep() {
    this.currentStepIndex += 1;
    this.calculateProgress();
  }

  // A step asked to go back (e.g. from employees back to locations to keep adjusting the structure).
  goBack() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex -= 1;
      this.calculateProgress();
    }
  }

  // Last step reached and the account now fits the target plan. For the Free flow, switch the account
  // to Free (the PUT now succeeds); otherwise reload to pick up the adjusted billing data.
  #finalize() {
    if (this.freePlanSubscriptionId) {
      this.#switchToFree(this.freePlanSubscriptionId);
      return;
    }

    // @HACK: find a better solution to reload the billing data
    setTimeout(() => {
      location.reload();
    });
  }

  #switchToFree(subscriptionId: string) {
    this.#subscriptionService
      .saveSubscription({
        subscriptionId,
        integrationPlus: false,
        invoicePeriod: InvoicePeriodType.MONTH,
        compliance: false,
        hrPlus: false,
        chat: false,
      })
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        // Return to the root and reload so the app picks up the new Free plan billing data.
        this.#router
          .navigate(['/'])
          .then(() => window.location.reload())
          .catch(() => window.location.reload());
      });
  }

  calculateProgress() {
    this.progress = ((this.currentStepIndex + 1) / this.steps.length) * 100;
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }
}
